import React, { useState, useMemo } from "react";
import { FaShieldAlt } from "react-icons/fa";
import EmiCalculator from "./EmiCalculator";
import "./Cibill.css";

const GadgetLoan = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    fatherName: "",
    aadhaarNumber: "",
    panNumber: "",
    phoneNumber: "",
    alternateNumber: "",
    incomeType: "",
    address: "",
    email: "",
  });

  const [calcData, setCalcData] = useState({
    productPrice: 50000,
    downPayment: 20,
    tenure: 6,
    interestRate: 18,
    processingFee: 2,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "", visible: false });

  // EMI Calculations
  const processingFeeAmount = useMemo(() => {
    return Math.round((calcData.productPrice * calcData.processingFee) / 100);
  }, [calcData.productPrice, calcData.processingFee]);

  const baseLoanAmount = useMemo(() => {
    return Math.max(0, calcData.productPrice - (calcData.productPrice * calcData.downPayment) / 100);
  }, [calcData.productPrice, calcData.downPayment]);

  const loanAmount = useMemo(() => {
    return baseLoanAmount + processingFeeAmount;
  }, [baseLoanAmount, processingFeeAmount]);

  const monthlyInterest = useMemo(() => calcData.interestRate / 12 / 100, [calcData.interestRate]);

  const emi = useMemo(() => {
    if (!loanAmount || !calcData.tenure || !monthlyInterest) return 0;
    const power = Math.pow(1 + monthlyInterest, calcData.tenure);
    const result = (loanAmount * monthlyInterest * power) / (power - 1);
    return isFinite(result) ? Math.round(result) : 0;
  }, [loanAmount, monthlyInterest, calcData.tenure]);

  const totalAmount = useMemo(() => Math.round(emi * calcData.tenure), [emi, calcData.tenure]);
  const totalInterest = useMemo(() => Math.max(0, Math.round(totalAmount - loanAmount)), [totalAmount, loanAmount]);

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required.";
    if (!formData.fatherName.trim()) newErrors.fatherName = "Father's name is required.";

    const aadhaarRegex = /^\d{12}$/;
    if (!formData.aadhaarNumber.trim()) newErrors.aadhaarNumber = "Aadhaar number is required.";
    else if (!aadhaarRegex.test(formData.aadhaarNumber)) newErrors.aadhaarNumber = "Enter a valid 12-digit Aadhaar number.";

    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
    if (!formData.panNumber.trim()) newErrors.panNumber = "PAN number is required.";
    else if (!panRegex.test(formData.panNumber)) newErrors.panNumber = "Enter a valid PAN number (e.g., ABCDE1234F).";

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = "Phone number is required.";
    else if (!phoneRegex.test(formData.phoneNumber)) newErrors.phoneNumber = "Enter a valid 10-digit mobile number.";

    if (formData.alternateNumber && !phoneRegex.test(formData.alternateNumber))
      newErrors.alternateNumber = "Enter a valid 10-digit alternate number.";
    if (!formData.incomeType) newErrors.incomeType = "Please select income type.";
    if (!formData.address.trim()) newErrors.address = "Address is required.";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) newErrors.email = "Email is required.";
    else if (!emailRegex.test(formData.email)) newErrors.email = "Enter a valid email address.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === "panNumber") {
      updatedValue = value.toUpperCase();
    } else if (name === "phoneNumber" || name === "alternateNumber") {
      updatedValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (name === "aadhaarNumber") {
      updatedValue = value.replace(/\D/g, '').slice(0, 12);
    }

    setFormData({ ...formData, [name]: updatedValue });
    setErrors({ ...errors, [name]: "" });
  };

  const showToast = (message, type = "success") => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showToast("Please fix the errors in the form.", "error");
      return;
    }

    setIsSubmitting(true);

    const URL = "https://script.google.com/macros/s/AKfycbwwCTCEQJCXlwYoZ2nF0qp0dStyjX-lD1nKoYENQgqgCzRfel9Fz0IG3rJfMPLqmu1r-g/exec";

    try {
      const params = new URLSearchParams();
      
      params.append('fullName', formData.fullName);
      params.append('fatherName', formData.fatherName);
      params.append('aadhaarNumber', formData.aadhaarNumber);
      params.append('panNumber', formData.panNumber);
      params.append('phoneNumber', formData.phoneNumber);
      params.append('alternateNumber', formData.alternateNumber || "");
      params.append('email', formData.email);
      params.append('incomeType', formData.incomeType);
      params.append('address', formData.address);

      params.append('productPrice', calcData.productPrice);
      params.append('downPayment', calcData.downPayment);
      params.append('loanAmount', loanAmount);
      params.append('tenure', calcData.tenure);
      params.append('interestRate', calcData.interestRate);
      params.append('emi', emi);
      params.append('totalInterest', totalInterest);
      params.append('totalAmount', totalAmount);

      console.log('Data being sent:', Object.fromEntries(params));

      // Also send to local proxy endpoint to guarantee persistence in primary Google Sheet & server backup
      try {
        await fetch('/api/submit-application', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sheetName: 'CIBIL & Gadget Loans',
            formType: 'CIBIL & Gadget Loans',
            fullName: formData.fullName,
            fatherName: formData.fatherName,
            email: formData.email,
            phone: formData.phoneNumber,
            aadhaarNumber: formData.aadhaarNumber,
            panNumber: formData.panNumber,
            address: formData.address,
            incomeType: formData.incomeType || 'N/A',
            loanAmount: loanAmount,
            emi: emi,
            position: 'CIBIL / Gadget Loan Application',
            experience: `Income: ${formData.incomeType || 'N/A'}`,
            portfolioUrl: `Aadhaar: ${formData.aadhaarNumber} | PAN: ${formData.panNumber}`,
            about: `Father: ${formData.fatherName} | Address: ${formData.address} | Loan: ₹${loanAmount} | EMI: ₹${emi}/mo`
          })
        });
      } catch (proxyErr) {
        console.warn('Backup endpoint submit error:', proxyErr);
      }

      const response = await fetch(URL, {
        method: "POST",
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      const responseText = await response.text();
      console.log('Response from server:', responseText);

      if (response.ok) {
        showToast("Quote generated successfully! Data has been saved to Google Sheets.", "success");
        console.log("Data successfully sent to Google Sheets");

        setFormData({
          fullName: "",
          fatherName: "",
          aadhaarNumber: "",
          panNumber: "",
          phoneNumber: "",
          alternateNumber: "",
          incomeType: "",
          address: "",
          email: "",
        });
        
        setCalcData({
          productPrice: 50000,
          downPayment: 20,
          tenure: 6,
          interestRate: 18,
          processingFee: 2,
        });
      } else {
        throw new Error(`Network response was not ok: ${response.status}`);
      }
    } catch (error) {
      console.error("Submission error:", error);
      showToast("There was an error generating the quote. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const emiResults = {
    loanAmount,
    emi,
    totalInterest,
    totalAmount,
    processingFeeAmount
  };

  return (
    <div className="cibil-container">
      <div className="cibil-header">
        <h1 className="cibil-title">Financial Health Check</h1>
        <p className="cibil-subtitle">
          Check your CIBIL score and calculate your loan EMI in one place
        </p>
      </div>

      <div className="cibil-content">
        <div className="cibil-form-section">
          <div className="form-header">
            <div className="form-icon">
              <FaShieldAlt className="icon-shield" />
            </div>
            <div>
              <h2 className="form-title">Check Your CIBIL Score</h2>
              <p className="form-subtitle">Get your free credit score instantly</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="cibil-form">
            <div className="form-group">
              <h3 className="section-title">Personal Information</h3>
              <div className="input-grid">
                <div className="input-field">
                  <label className="input-label">Full Name</label>
                  <div className="input-wrapper">
                    <input
                      name="fullName"
                      type="text"
                      value={formData.fullName}
                      onChange={handleChange}
                      className={`cibil-input ${errors.fullName ? 'input-error' : ''}`}
                      placeholder="Enter your full name"
                      aria-invalid={!!errors.fullName}
                    />
                  </div>
                  {errors.fullName && <p className="error-text">{errors.fullName}</p>}
                </div>
                <div className="input-field">
                  <label className="input-label">Father's Name</label>
                  <div className="input-wrapper">
                    <input
                      name="fatherName"
                      type="text"
                      value={formData.fatherName}
                      onChange={handleChange}
                      className={`cibil-input ${errors.fatherName ? 'input-error' : ''}`}
                      placeholder="Enter father's name"
                      aria-invalid={!!errors.fatherName}
                    />
                  </div>
                  {errors.fatherName && <p className="error-text">{errors.fatherName}</p>}
                </div>
              </div>
            </div>

            <div className="form-group">
              <h3 className="section-title">Document Details</h3>
              <div className="input-grid">
                <div className="input-field">
                  <label className="input-label">Aadhaar Number</label>
                  <div className="input-wrapper">
                    <input
                      name="aadhaarNumber"
                      type="text"
                      value={formData.aadhaarNumber}
                      onChange={handleChange}
                      className={`cibil-input ${errors.aadhaarNumber ? 'input-error' : ''}`}
                      placeholder="Enter 12-digit Aadhaar"
                      maxLength="12"
                      aria-invalid={!!errors.aadhaarNumber}
                    />
                  </div>
                  {errors.aadhaarNumber && <p className="error-text">{errors.aadhaarNumber}</p>}
                </div>
                <div className="input-field">
                  <label className="input-label">PAN Number</label>
                  <div className="input-wrapper">
                    <input
                      name="panNumber"
                      type="text"
                      value={formData.panNumber}
                      onChange={handleChange}
                      className={`cibil-input ${errors.panNumber ? 'input-error' : ''}`}
                      placeholder="Enter PAN number"
                      maxLength="10"
                      aria-invalid={!!errors.panNumber}
                    />
                  </div>
                  {errors.panNumber && <p className="error-text">{errors.panNumber}</p>}
                </div>
              </div>
            </div>

            <div className="form-group">
              <h3 className="section-title">Contact Information</h3>
              <div className="input-grid">
                <div className="input-field">
                  <label className="input-label">Phone Number</label>
                  <div className="input-wrapper">
                    <input
                      name="phoneNumber"
                      type="text"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      className={`cibil-input ${errors.phoneNumber ? 'input-error' : ''}`}
                      placeholder="Enter 10-digit mobile number"
                      maxLength="10"
                      aria-invalid={!!errors.phoneNumber}
                    />
                  </div>
                  {errors.phoneNumber && <p className="error-text">{errors.phoneNumber}</p>}
                </div>
                <div className="input-field">
                  <label className="input-label">Alternate Number</label>
                  <div className="input-wrapper">
                    <input
                      name="alternateNumber"
                      type="text"
                      value={formData.alternateNumber}
                      onChange={handleChange}
                      className={`cibil-input ${errors.alternateNumber ? 'input-error' : ''}`}
                      placeholder="Optional alternate number"
                      maxLength="10"
                      aria-invalid={!!errors.alternateNumber}
                    />
                  </div>
                  {errors.alternateNumber && <p className="error-text">{errors.alternateNumber}</p>}
                </div>
              </div>
              <div className="input-field">
                <label className="input-label">Email Address</label>
                <div className="input-wrapper">
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`cibil-input ${errors.email ? 'input-error' : ''}`}
                    placeholder="Enter email address"
                    aria-invalid={!!errors.email}
                  />
                </div>
                {errors.email && <p className="error-text">{errors.email}</p>}
              </div>
            </div>

            <div className="form-group">
              <h3 className="section-title">Additional Details</h3>
              <div className="input-grid">
                <div className="input-field">
                  <label className="input-label">Income Type</label>
                  <div className="input-wrapper">
                    <select
                      name="incomeType"
                      value={formData.incomeType}
                      onChange={handleChange}
                      className={`cibil-input cibil-select ${errors.incomeType ? 'input-error' : ''}`}
                      aria-invalid={!!errors.incomeType}
                    >
                      <option value="">Select Income Type</option>
                      <option value="Salaried">Salaried</option>
                      <option value="Self Employed">Self Employed</option>
                    </select>
                  </div>
                  {errors.incomeType && <p className="error-text">{errors.incomeType}</p>}
                </div>
                <div className="input-field">
                  <label className="input-label">Address</label>
                  <div className="input-wrapper">
                    <input
                      name="address"
                      type="text"
                      value={formData.address}
                      onChange={handleChange}
                      className={`cibil-input ${errors.address ? 'input-error' : ''}`}
                      placeholder="Enter your address"
                      aria-invalid={!!errors.address}
                    />
                  </div>
                  {errors.address && <p className="error-text">{errors.address}</p>}
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`submit-btn ${isSubmitting ? 'loading' : ''}`}
              aria-disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner"></div>
                  Generating Quote...
                </>
              ) : (
                "Generate Quote"
              )}
            </button>
          </form>
        </div>
        <div className="emi-calculator-section">
          <EmiCalculator calcData={calcData} setCalcData={setCalcData} emiResults={emiResults} />
        </div>
      </div>

      {toast.visible && (
        <div className={`toast ${toast.type}`}>
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
};

export default GadgetLoan;